import { useEffect, useRef, useState } from 'react'
import { AlertCircle, ImagePlus, Loader2, RotateCw, Trash2, Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { attachmentsApi, type FormAttachment } from '@/api/attachmentsApi'
import { ATTACHMENT_MAX_BYTES, IMAGE_FIELD_ACCEPTED_MIME } from '@/types/cgt'
import { formatBytes } from './AttachmentPreview'
import { AttachmentPreviewDialog } from './AttachmentPreviewDialog'
import { SYSTEM_LABEL_LAYOUT, type ResolvedLabelLayout } from '@/lib/fieldLayout'
import { FieldRow } from './FieldRow'

/**
 * A picked-but-not-uploaded file, for builder preview mode.
 *
 * Carries the object URL as well as the name: preview mode has no submission
 * behind it, so there is no download URL to fetch and the only way to show the
 * author their own picture is the blob they just chose.
 */
export interface PreviewImage {
  fileName: string
  sizeBytes: number
  objectUrl: string
}

interface Props {
  submissionId: string
  fieldId: string
  label: string
  helpText?: string
  /** The stored picture, when the field has one. */
  attachment?: FormAttachment
  required: boolean
  locked: boolean
  /** True while this field's upload/remove mutation is in flight. */
  busy: boolean
  /** Set when the completion attempt flagged this field as empty. */
  error?: string
  onUpload: (file: File) => void
  onRemove: () => void
  /** Builder preview: fully interactive, but nothing is persisted. */
  preview?: boolean
  previewImage?: PreviewImage | null
  onPreviewImageChange?: (image: PreviewImage | null) => void
  /** Where the label sits (z8nrz7djyx); see lib/fieldLayout. Absent = the system default. */
  layout?: ResolvedLabelLayout
  stacked?: boolean
}

/**
 * An image field: the picture IS the value, shown inline at a size you can
 * actually read.
 *
 * ## Why this is not the attachment control
 *
 * `AttachmentControl` is a status ICON beside a field — deliberately small,
 * because a form with eight attachable fields should read as a column of fields
 * with a status rail, not a stack of upload widgets. The file there is evidence
 * *next to* a value, and you click to see it.
 *
 * Here the picture is the entire content of the field. A form whose Image field
 * renders as a paperclip has failed to show the one thing it holds — so this
 * renders the image itself, inline, at a size that is legible on the form and
 * still small enough that a table of them stays a table (the reference is a
 * parts list: a thumbnail per row beside the SKU). Full size is one click away
 * in the same preview panel every other attachment uses.
 *
 * Storage underneath is exactly the attachment pipeline — one `FormAttachment`
 * row keyed on (submissionId, fieldId), presign → PUT → confirm, replace-by-
 * upsert, audited. Nothing new is stored and nothing is duplicated; see
 * `valueIsAttachment` in types/cgt.ts for why the field nonetheless does not
 * carry an `attachment` config of its own.
 */
export function ImageField({
  submissionId,
  fieldId,
  label,
  helpText,
  attachment,
  required,
  locked,
  busy,
  error,
  onUpload,
  onRemove,
  preview = false,
  previewImage = null,
  onPreviewImageChange,
  layout = SYSTEM_LABEL_LAYOUT,
  stacked = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [viewerOpen, setViewerOpen] = useState(false)

  const message = error ?? localError
  const missing = required && !attachment && !previewImage

  const pick = (file: File | undefined) => {
    if (!file) return
    setLocalError(null)
    if (!IMAGE_FIELD_ACCEPTED_MIME.includes(file.type)) {
      setLocalError('Only JPEG and PNG images can be used here.')
      return
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setLocalError(
        `Image is ${formatBytes(file.size)} — the limit is ${formatBytes(ATTACHMENT_MAX_BYTES)}.`,
      )
      return
    }
    if (preview) {
      onPreviewImageChange?.({
        fileName: file.name,
        sizeBytes: file.size,
        objectUrl: URL.createObjectURL(file),
      })
      return
    }
    onUpload(file)
  }

  const clear = () => {
    setLocalError(null)
    if (preview) {
      onPreviewImageChange?.(null)
      return
    }
    onRemove()
  }

  // Laid out like every other field on the form: label to the left, control to
  // the right, stacking on a narrow screen. `items-start` and the label's
  // `sm:pt-2` follow the Textarea branch, the other control tall enough that
  // centring its label against it reads as misalignment.
  //
  // The label points at the picker button, the one control that is always
  // there to receive focus; with an image showing, at the thumbnail.
  return (
    <FieldRow
      label={label}
      helpText={helpText}
      required={required}
      layout={layout}
      stacked={stacked}
      tall
      controlClassName='flex flex-1 items-start gap-3'
    >
      {({ id }) => (
        <>
          {attachment || previewImage ? (
            <StoredImage
              id={id}
              submissionId={submissionId}
              fieldId={fieldId}
              fileName={attachment?.fileName ?? previewImage!.fileName}
              sizeBytes={attachment?.sizeBytes ?? previewImage!.sizeBytes}
              storageKey={attachment?.storageKey}
              previewUrl={previewImage?.objectUrl}
              onOpen={() => setViewerOpen(true)}
            />
          ) : (
            <button
              id={id}
              type='button'
              disabled={locked || busy}
              onClick={() => inputRef.current?.click()}
              className={cn(
                'flex h-20 w-28 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed bg-muted/20 text-xs text-muted-foreground transition-colors',
                missing ? 'border-destructive/60 text-destructive' : 'border-border',
                locked || busy
                  ? 'cursor-not-allowed opacity-60'
                  : 'hover:border-primary/50 hover:bg-muted/40 hover:text-foreground',
              )}
            >
              {busy ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <>
                  <ImagePlus className='h-4 w-4' />
                  <span>Add image</span>
                </>
              )}
            </button>
          )}

          {!locked && (attachment || previewImage) && (
            <div className='flex flex-col gap-1'>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                className='h-7 justify-start px-2 text-xs'
              >
                <Upload className='mr-1.5 h-3.5 w-3.5' />
                Replace
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                disabled={busy}
                onClick={clear}
                className='h-7 justify-start px-2 text-xs text-destructive hover:text-destructive'
              >
                <Trash2 className='mr-1.5 h-3.5 w-3.5' />
                Remove
              </Button>
            </div>
          )}
          {message && (
            <p className='flex items-center gap-1 self-center text-xs text-destructive'>
              <AlertCircle className='h-3 w-3 shrink-0' />
              {message}
            </p>
          )}

          <input
            ref={inputRef}
            type='file'
            accept={IMAGE_FIELD_ACCEPTED_MIME.join(',')}
            className='hidden'
            onChange={e => {
              pick(e.target.files?.[0])
              // Cleared so picking the SAME file again still fires a change event —
              // which is exactly what "replace with the corrected scan" looks like.
              e.target.value = ''
            }}
          />

          {viewerOpen && attachment && (
            <AttachmentPreviewDialog
              submissionId={submissionId}
              fieldId={fieldId}
              fileName={attachment.fileName}
              sizeBytes={attachment.sizeBytes}
              open={viewerOpen}
              onOpenChange={setViewerOpen}
            />
          )}
        </>
      )}
    </FieldRow>
  )
}

/**
 * The thumbnail itself.
 *
 * Fetches its own download URL on mount — a URL string, not the bytes — the way
 * `AttachmentThumb` does, so the picture appears without anyone clicking. In
 * preview mode there is no stored object, so the caller's blob URL is used
 * directly and no request is made at all.
 */
function StoredImage({
  id,
  submissionId,
  fieldId,
  fileName,
  sizeBytes,
  storageKey,
  previewUrl,
  onOpen,
}: {
  id: string
  submissionId: string
  fieldId: string
  fileName: string
  sizeBytes: number
  storageKey?: string
  previewUrl?: string
  onOpen: () => void
}) {
  const [url, setUrl] = useState<string | null>(previewUrl ?? null)
  const [failed, setFailed] = useState(false)
  const [retryTick, setRetryTick] = useState(0)

  useEffect(() => {
    if (previewUrl) {
      setUrl(previewUrl)
      return
    }
    let cancelled = false
    // Cleared up front: after a replace the storageKey changes, and the old URL
    // must not keep painting a picture that is no longer the field's value.
    setUrl(null)
    setFailed(false)
    attachmentsApi
      .getDownloadUrl(submissionId, fieldId)
      .then(res => {
        if (!cancelled) setUrl(res.url)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [submissionId, fieldId, storageKey, previewUrl, retryTick])

  if (failed) {
    return (
      <button
        id={id}
        type='button'
        title={`Couldn't load ${fileName} — click to retry`}
        onClick={() => setRetryTick(t => t + 1)}
        className='flex h-20 w-28 shrink-0 items-center justify-center rounded-lg border border-dashed border-destructive/40 text-destructive transition-colors hover:bg-destructive/5'
      >
        <RotateCw className='h-4 w-4' />
      </button>
    )
  }

  return (
    <button
      id={id}
      type='button'
      onClick={onOpen}
      title={`${fileName} — ${formatBytes(sizeBytes)}. Click to view full size.`}
      className='h-20 w-28 shrink-0 overflow-hidden rounded-lg border bg-muted/40 transition-colors hover:border-primary/50'
    >
      {url ? (
        <img src={url} alt={fileName} className='h-full w-full object-cover' />
      ) : (
        <span className='flex h-full w-full items-center justify-center'>
          <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
        </span>
      )}
    </button>
  )
}
